interface HandlerData {
    throttleMs: number;
    lastCall: number;
    doOnce: boolean;
}

/**
 * Simple Observable for use by Publishers and Subscribers.
 *
 * A Publisher is any object that invokes #publish on the event.
 * A Subscriber is an object that registers a callback with #subscribe or #doOnce.
 */
export default class Event {
    _handlers: Map<(sender: Object, eventArgs: Object) => void, HandlerData>;

    constructor() {
        this._handlers = new Map();
    }

    /**
     * Subscribe the callback to published events. Callbacks invocations
     * will be appended to the end of the event loop.
     * @param callback function to invoke on #publish, taking a sender and eventArgs from the #publish call.
     * @param throttleMs minimum number of milliseconds to wait between invocations of this callback in response to calls to this Event's publish.
     * #publish calls that occur during this timeout will not be forwarded to the callback.
     */
    subscribe(callback: (sender: Object, eventArgs: Object) => void, throttleMs?: number = null) {
        if (!callback) {
            throw new Error('callback must be defined');
        }
        if (!(callback instanceof Function)) {
            throw new Error('callback must be a function');
        }
        this._handlers.set(callback, {
            throttleMs,
        });
    }

    /**
     * Remove the given callback from the list of functions that will be invoked on calls to #publish.
     * @param callback
     */
    unsubscribe(callback: (sender: Object, eventArgs: Object) => void) {
        if (!callback || !(callback instanceof Function)) {
            throw new Error('callback must be defined and must be a function');
        }
        this._handlers.delete(callback);
    }

    /**
     * As subscribe, but when the callback has been invoked once, automatically unsubscribe the callback from this Event.
     * @param callback
     */
    doOnce(callback: (sender: Object, eventArgs: Object) => void) {
        if (!callback || !(callback instanceof Function)) {
            throw new Error('callback must be defined and must be a function');
        }
        this._handlers.set(callback, {doOnce: true});
    }

    /**
     * Schedule an invocation of each eligible callback that is subscribed to this event using
     * the given sender and eventArgs.
     * @param sender
     * @param eventArgs
     */
    publish(sender: Object, eventArgs: Object) {
        this._handlers.forEach(
            (data: HandlerData, callback: (sender: Object, eventArgs: Object) => void) => {
                if (data.doOnce) {
                    this.unsubscribe(callback);
                }
                if (data.throttleMs) {
                    let now = Date.now();
                    let diff = now - data.lastCall;
                    /**
                     * If it has been longer than the throttleMs (or maximum frequency)
                     * then set the lastCall to now and invoke the callback.
                     */
                    if (!data.lastCall || diff > data.throttleMs) {
                        data.lastCall = now;
                        setTimeout(callback, 0, sender, eventArgs);
                    }
                } else {
                    setTimeout(callback, 0, sender, eventArgs);
                }
            }
        );
    }
}
